import { useEffect, useState } from 'react';
import { management } from '@solimat/solimat-web-endpoint';
import * as yup from 'yup';

const managementSchema = yup.object({
    Id: yup.mixed<string | number>().defined().test(
        'identifier', 'Identificador de gestion no valido',
        value => typeof value === 'string' || typeof value === 'number'
    ),
    Titulo: yup.string().defined(),
    Tipo: yup.number().required(),
    Descripcion: yup.string().nullable().optional(),
});

const groupSchema = yup.object({
    Titulo: yup.string().defined(),
    Descripcion: yup.string().nullable().optional(),
    Gestiones: yup.array(managementSchema).ensure(),
});

const responseSchema = yup.object({
    Activos: yup.object({
        Gestiones: yup.array(managementSchema).ensure(),
        Grupos: yup.array(groupSchema).ensure(),
    }).nullable().default(null),
});

export type PrestacionManagement = yup.InferType<typeof managementSchema>;
export type PrestacionManagementGroup = yup.InferType<typeof groupSchema>;

const apiManagement = new management.ManagementService(
    import.meta.env.VITE_REACT_APP_MANAGEMENT_PATH || '',
    import.meta.env.VITE_REACT_APP_MANAGEMENT_KEY || ''
);

interface ManagementResult {
    expedienteId: string | null;
    loading: boolean;
    error: Error | null;
    items: PrestacionManagement[];
    groups: PrestacionManagementGroup[];
}

const emptyResult: ManagementResult = {
    expedienteId: null,
    loading: false,
    error: null,
    items: [],
    groups: [],
};

export default function useGetMyManagementByPrestacion(expedienteId: string | number | null) {
    const candidate = expedienteId === null ? '' : String(expedienteId).trim();
    const currentId = /^[1-9]\d*$/.test(candidate) ? candidate : null;
    const [result, setResult] = useState<ManagementResult>(emptyResult);

    useEffect(() => {
        if (!currentId) {
            setResult(emptyResult);
            return;
        }

        let active = true;
        setResult({ ...emptyResult, expedienteId: currentId, loading: true });

        const loadManagement = async () => {
            try {
                const response = await apiManagement.getMyManagementByPrestacion(currentId);
                const data = await responseSchema.validate(response.data);
                if (active) {
                    setResult({
                        expedienteId: currentId,
                        loading: false,
                        error: null,
                        items: data.Activos?.Gestiones ?? [],
                        groups: data.Activos?.Grupos ?? [],
                    });
                }
            } catch (error: unknown) {
                if (active) {
                    setResult({
                        ...emptyResult,
                        expedienteId: currentId,
                        error: error instanceof Error ? error : new Error('No se pudieron cargar los formularios.'),
                    });
                }
            }
        };

        void loadManagement();
        return () => { active = false; };
    }, [currentId]);

    return result.expedienteId === currentId
        ? result
        : { ...emptyResult, expedienteId: currentId, loading: currentId !== null };
}